import { z } from 'zod'

import {
  ConsentBasis,
  type SummaryFeedbackAdminAudioUrl,
  type SummaryFeedbackAdminDetail,
  SummaryFeedbackAdminDetailAudioStatusEnum,
  SummaryFeedbackAdminDetailSummaryWithheldReasonEnum,
  type SummaryFeedbackAdminList,
  type SummaryFeedbackAdminListItem,
  type SummaryFeedbackAdminStats,
  SummaryFeedbackRating,
} from './generated/api'
import { fetchAPI } from '.'

export type SummaryFeedbackDetail = SummaryFeedbackAdminDetail
export type SummaryFeedbackAudioUrl = SummaryFeedbackAdminAudioUrl
export type SummaryFeedbackList = SummaryFeedbackAdminList
export type SummaryFeedbackListItem = SummaryFeedbackAdminListItem
export type SummaryFeedbackStats = SummaryFeedbackAdminStats
export type SummaryFeedbackVersion = SummaryFeedbackAdminStats['byVersion'][number]
export type SummaryFeedbackStatsBucket = 'day' | 'week'

export const SUMMARY_FEEDBACK_PAGE_SIZE = 100

const timestamp = z.string().datetime({ offset: true })
const LOOPBACK_HOSTS = ['127.0.0.1', 'localhost']
const parseUrl = (value: string) => {
  try {
    return new URL(value)
  } catch {
    return null
  }
}
// Local MinIO presigns plain-http loopback URLs.
const isPlayableUrl = (value: string) => {
  const url = parseUrl(value)
  return url?.protocol === 'https:' || (url?.protocol === 'http:' && LOOPBACK_HOSTS.includes(url.hostname))
}

const detailSchema: z.ZodType<SummaryFeedbackDetail, unknown> = z.object({
  id: z.guid(),
  meetingId: z.guid(),
  rating: z.nativeEnum(SummaryFeedbackRating),
  feedback: z.string().nullable(),
  submittedAt: timestamp,
  createdAt: timestamp,
  clearedAt: timestamp.nullable(),
  reporterEmail: z.string().nullable(),
  reporterIsInternal: z.boolean(),
  consentBasis: z.nativeEnum(ConsentBasis),
  summarySha256: z.string(),
  clientSummarySha256: z.string().nullable(),
  summaryDrift: z.boolean(),
  summaryModel: z.string().nullable(),
  summaryPromptVersion: z.string().nullable(),
  promptLabel: z.string().nullable().default(null),
  summaryBody: z.string().nullable(),
  summaryWithheldReason: z.nativeEnum(SummaryFeedbackAdminDetailSummaryWithheldReasonEnum).nullable(),
  meetingDeleted: z.boolean(),
  audioStatus: z.nativeEnum(SummaryFeedbackAdminDetailAudioStatusEnum),
  audioExpiresAt: timestamp.nullable(),
  viewerMeetingId: z.guid().nullable(),
})

const listItemSchema: z.ZodType<SummaryFeedbackListItem, unknown> = z.object({
  id: z.guid(),
  meetingId: z.guid(),
  rating: z.nativeEnum(SummaryFeedbackRating),
  createdAt: timestamp,
  submittedAt: timestamp,
  clearedAt: timestamp.nullable(),
  reporterEmail: z.string().nullable(),
  reporterIsInternal: z.boolean(),
  feedbackPreview: z.string().nullable(),
  consentBasis: z.nativeEnum(ConsentBasis),
  summaryPromptVersion: z.string().nullable(),
  promptLabel: z.string().nullable().default(null),
  summaryModel: z.string().nullable().default(null),
  hasSummarySnapshot: z.boolean(),
  meetingDeleted: z.boolean(),
  isSuperseded: z.boolean().default(false),
})

const listSchema: z.ZodType<SummaryFeedbackList, unknown> = z.object({
  items: z.array(listItemSchema),
  nextCursor: z.string().nullable(),
  total: z.number().int().nonnegative().default(0),
})

export const ratingCountsSchema = z.object({
  up: z.number().int().nonnegative(),
  down: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  pctUp: z.number().min(0).max(100).nullable(),
})
export type RatingCounts = z.infer<typeof ratingCountsSchema>

const countsSchema = ratingCountsSchema.extend({ meetings: z.number().int().nonnegative().default(0) })

const statsSchema: z.ZodType<SummaryFeedbackStats, unknown> = z.object({
  overall: countsSchema,
  buckets: z.array(countsSchema.extend({ start: timestamp })),
  byPrompt: z.array(
    countsSchema.extend({ promptVersion: z.string().nullable(), promptLabel: z.string().nullable().default(null) })
  ),
  byVersion: z
    .array(
      countsSchema.extend({
        promptVersion: z.string().nullable(),
        promptLabel: z.string().nullable().default(null),
        model: z.string().nullable(),
      })
    )
    .default([]),
})

const audioUrlSchema: z.ZodType<SummaryFeedbackAudioUrl, unknown> = z.object({
  url: z.string().refine(isPlayableUrl),
  expiresAt: timestamp,
})

const BASE_PATH = '/api/v1/internal/meeting-summary-feedback'

export const parseResponse = <T>(schema: z.ZodType<T, unknown>, data: unknown, endpoint = 'Summary feedback'): T => {
  const result = schema.safeParse(data)
  if (result.success) return result.data
  console.warn(`${endpoint} response schema mismatch`, {
    issues: result.error.issues.map(({ path, code }) => ({ path, code })),
  })
  throw result.error
}

export const listSummaryFeedback = async (
  {
    rating,
    promptVersion,
    summaryModel,
    since,
    until,
    cursor,
    limit = SUMMARY_FEEDBACK_PAGE_SIZE,
  }: {
    rating?: SummaryFeedbackRating
    promptVersion?: string
    summaryModel?: string
    since?: string
    until?: string
    cursor?: string
    limit?: number
  },
  signal?: AbortSignal
) => {
  const response = await fetchAPI<unknown>({
    method: 'GET',
    url: BASE_PATH,
    params: {
      limit,
      ...(rating ? { rating } : {}),
      ...(promptVersion ? { promptVersion } : {}),
      ...(summaryModel ? { summaryModel } : {}),
      ...(since ? { since } : {}),
      ...(until ? { until } : {}),
      ...(cursor ? { cursor } : {}),
    },
    signal,
    expectedStatuses: [403],
  })
  return parseResponse(listSchema, response.data)
}

export const getSummaryFeedbackStats = async (
  {
    since,
    until,
    bucket,
    promptVersion,
    summaryModel,
  }: {
    since: string
    until: string
    bucket: SummaryFeedbackStatsBucket
    promptVersion?: string
    summaryModel?: string
  },
  signal?: AbortSignal
) => {
  const response = await fetchAPI<unknown>({
    method: 'GET',
    url: `${BASE_PATH}/stats`,
    params: {
      since,
      until,
      bucket,
      ...(promptVersion ? { promptVersion } : {}),
      ...(summaryModel ? { summaryModel } : {}),
    },
    signal,
    expectedStatuses: [403],
  })
  return parseResponse(statsSchema, response.data)
}

const feedbackPath = (feedbackId: string) => `${BASE_PATH}/${encodeURIComponent(feedbackId)}`

export const getSummaryFeedback = async (feedbackId: string, signal?: AbortSignal) => {
  const response = await fetchAPI<unknown>({
    method: 'GET',
    url: feedbackPath(feedbackId),
    signal,
    expectedStatuses: [403, 404],
  })
  return parseResponse(detailSchema, response.data)
}

export const createSummaryFeedbackAudioUrl = async (feedbackId: string) => {
  const response = await fetchAPI<unknown>({
    method: 'POST',
    url: `${feedbackPath(feedbackId)}/audio-url`,
    expectedStatuses: [403, 404, 409, 410],
  })
  return parseResponse(audioUrlSchema, response.data)
}
